import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { metricData } from './metric_data'
import Categories from './categories'
import MetricSelector from './metric_selector'
import Chart from './chart'
import './wellness.css'

class NewWellness extends Component {
  constructor(props) {
    super(props)

    this.updateSelected = this.updateSelected.bind(this)
    this.state = {
      selectedMetric: 'bodyShapeRating',
      selectedCategory: 'bodyShape',
    }
  }

  // Compute selectors dynamically based on current props
  getSelectors() {
    const { wellnessMetrics, hwVersion, bfpAccess } = this.props

    // Get vatMass from database (only for hw_version=7)
    const apiVatMass = wellnessMetrics.get('vatMass')
    const vatMassRounded =
      apiVatMass !== undefined && apiVatMass !== null
        ? Math.round(apiVatMass * 10) / 10
        : 0

    // Get metabolicAgeYears from database (only for hw_version=7)
    const apiMetabolicAge = wellnessMetrics.get('metabolicAgeYears')
    const metabolicAgeValid =
      apiMetabolicAge !== undefined &&
      apiMetabolicAge !== null &&
      apiMetabolicAge > 0

    // Add vatMass to bodyComp selectors for hw_version=7 only if > 0
    let bodyCompMetrics =
      bfpAccess === 1 ? ['bfp', 'weight', 'fatMass', 'leanMass'] : ['weight']

    // Add vatMass for hw_version=7 if > 0
    const isSnapScan = Number(hwVersion) === 7
    if (isSnapScan && vatMassRounded > 0 && bfpAccess === 1) {
      bodyCompMetrics = [...bodyCompMetrics, 'vatMass']
    }

    // Replace trunkLegVolRatio with metabolicAgeYears for hw_version=7 (only if > 0)
    let bodyShapeMetrics = ['bodyShapeRating', 'wc', 'whr']
    if (isSnapScan) {
      if (metabolicAgeValid) {
        bodyShapeMetrics = [...bodyShapeMetrics, 'metabolicAgeYears']
      }
    } else {
      bodyShapeMetrics = [...bodyShapeMetrics, 'trunkLegVolRatio']
    }

    return {
      bodyShape: bodyShapeMetrics,
      bodyComp: bodyCompMetrics,
      fitnessLevel: ['bmr'],
    }
  }

  updateSelected(key, value) {
    if (this.state[key] !== value) {
      if (key === 'selectedCategory') {
        const selectors = this.getSelectors()
        this.setState({ selectedMetric: selectors[value][0] })
      }
      this.setState({ [key]: value })
    }
  }

  getMetricValue(metric) {
    const { wellnessMetrics } = this.props
    let metricValue = wellnessMetrics.get(metric)

    // For VAT Mass: only use database value (from scan_snap table for HW v7)
    // Do not calculate client-side as the previous calculation was incorrect
    if (metric === 'vatMass') {
      const apiVatMass = wellnessMetrics.get('vatMass')
      metricValue =
        apiVatMass !== undefined && apiVatMass !== null ? apiVatMass : null
    }

    return metricValue
  }

  getEnhancedWellnessMetrics() {
    const { wellnessMetrics } = this.props

    // VAT Mass should only come from the database (scan_snap table)
    // Previous calculation was incorrect, so we don't calculate client-side
    return wellnessMetrics
  }

  renderHistogram(metric) {
    const { healthRanges, units } = this.props
    const { name } = metricData(units)[metric]

    let data

    if (healthRanges.get(metric)) {
      data = healthRanges.get(metric).get('populationHistogram')
    }

    // Get the metric value using the helper function
    const metricValue = this.getMetricValue(metric)

    if (data) {
      return (
        <Chart
          metric={metric}
          ranges={healthRanges.get(metric).get('ranges')}
          value={metricValue}
          data={data}
          title={name}
        />
      )
    }

    // For vatMass, show a basic display even without histogram data
    if (metric === 'vatMass') {
      return (
        <div className="pb-3">
          <h3>{name}</h3>
          <p>
            Your VAT Mass:{' '}
            <strong>{metricValue ? metricValue.toFixed(1) : 'N/A'}</strong>{' '}
            {metricData(units)[metric].units}
          </p>
        </div>
      )
    }

    // For metabolicAgeYears, show a basic display even without histogram data
    if (metric === 'metabolicAgeYears') {
      return (
        <div className="pb-3">
          <h3>{name}</h3>
          <p>
            Your Metabolic Age:{' '}
            <strong>{metricValue ? Math.round(metricValue) : 'N/A'}</strong>{' '}
            {metricData(units)[metric].units}
          </p>
        </div>
      )
    }

    return null
  }

  render() {
    const { selectedMetric, selectedCategory } = this.state
    const enhancedWellnessMetrics = this.getEnhancedWellnessMetrics()
    const selectors = this.getSelectors()

    return (
      <div className="mb-5 print-break-after">
        <div>
          <h2>Wellness Metrics</h2>
        </div>
        <div className="card card-default p-0" style={{ borderRadius: 0 }}>
          <div className="card-body p-0">
            <Categories
              selectedCategory={selectedCategory}
              updateSelected={this.updateSelected}
            />
            <div className="row mx-0">
              <div className="col-sm-4 p-3 card-container">
                <MetricSelector
                  selectedMetric={this.state.selectedMetric}
                  wellnessMetrics={enhancedWellnessMetrics}
                  metrics={selectors[selectedCategory]}
                  units={this.props.units}
                  updateSelected={this.updateSelected}
                />
              </div>
              <div className="col-sm-8 px-3 pb-3">
                <div style={{ paddingTop: '30px', marginBottom: '40px' }}>
                  <h3>What is it?</h3>
                  {metricData(this.props.units)[selectedMetric].description}
                  {selectedMetric !== 'weight' &&
                    selectedMetric !== 'vatMass' &&
                    selectedMetric !== 'metabolicAgeYears' && (
                      <div>
                        <h3>How do I compare?</h3>
                        <p>
                          The chart below shows how you compare against
                          recommended healthy ranges as well as how you compare
                          to other Fit3D users with your same gender and similar
                          age.
                        </p>
                      </div>
                    )}
                </div>
                <div>{this.renderHistogram(selectedMetric)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

NewWellness.propTypes = {
  facility: PropTypes.object,
  histograms: PropTypes.object,
  healthRanges: PropTypes.object,
  wellnessMetrics: PropTypes.object,
  gender: PropTypes.string,
  units: PropTypes.string.isRequired,
  bfpAccess: PropTypes.number.isRequired,
  hwVersion: PropTypes.number,
}

export default NewWellness
